/* eslint-disable react-refresh/only-export-components */
import {
  ArticleDesc,
  ItemCatalogue2Desc,
  LigneDePanierDesc,
} from 'api-types/ffconnect-hal';
import {State} from '@synako/halitrek';
import React, {createContext, useContext, useMemo} from 'react';

export type Destination = 'panier' | 'cadencier' | 'liste';

interface ListItemsContextValue {
  /**
   * Vérifie si un article est déjà dans la liste active
   */
  isItemInList: (code_article: string) => boolean;
  /**
   * Ajoute un article à la liste active (cadencier ou liste)
   */
  handleAddToList:
    | ((
        item_catalogue: State<ItemCatalogue2Desc>,
        setIsAdding: (adding: boolean) => void,
      ) => void)
    | undefined;
  /**
   * Destination de l'ajout : panier, cadencier ou liste
   */
  destination: Destination;
  /**
   * Lignes du panier actif (partagées pour éviter N appels identiques)
   */
  lignesDePanier: State<LigneDePanierDesc>[] | undefined;
}

export const ListItemsContext = createContext<ListItemsContextValue>({
  isItemInList: () => false,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  handleAddToList: () => {},
  destination: 'panier',
  lignesDePanier: undefined,
});

interface ListItemsProviderProps {
  children: React.ReactNode;
  produits_liste?: State<ArticleDesc>[];
  handleAddToList:
    | ((
        item_catalogue: State<ItemCatalogue2Desc>,
        setIsAdding: (adding: boolean) => void,
      ) => void)
    | undefined;
  destination?: Destination;
  lignesDePanier?: State<LigneDePanierDesc>[];
}

/**
 * Provider pour le contexte des listes saisonnières
 * Expose la destination d'ajout (panier, cadencier ou liste) et la fonction d'ajout.
 */
export const ListItemsProvider: React.FC<ListItemsProviderProps> = ({
  children,
  produits_liste,
  handleAddToList,
  destination = 'panier',
  lignesDePanier,
}) => {
  const contextValue = useMemo(() => {
    const isItemInList = (code_article: string) => {
      if (!produits_liste) {
        return false;
      }

      return produits_liste.some(
        (p) => p.data.tolede.code_article === code_article,
      );
    };

    return {
      isItemInList,
      handleAddToList,
      destination,
      lignesDePanier,
    };
  }, [handleAddToList, produits_liste, destination, lignesDePanier]);

  return (
    <ListItemsContext.Provider value={contextValue}>
      {children}
    </ListItemsContext.Provider>
  );
};

/**
 * Hook pour accéder au contexte des articles dans la liste
 */
export const useListItems = () => useContext(ListItemsContext);
